import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import CardActions from '@mui/material/CardActions';
import { useTheme, alpha } from '@mui/material/styles';
import { motion } from 'framer-motion';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AirplanemodeActiveIcon from '@mui/icons-material/AirplanemodeActive';
import BusinessIcon from '@mui/icons-material/Business';
import EventIcon from '@mui/icons-material/Event';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckIcon from '@mui/icons-material/Check';
import GridContainer from '../common/GridContainer';
import GridItem from '../common/GridItem';
import FloatingAnimation from '../common/FloatingAnimation';

interface ServiceType {
  id: number;
  title: string;
  description: string;
  image: string;
  icon: React.ReactNode;
  features: string[];
}

const services: ServiceType[] = [
  {
    id: 1,
    title: 'Airport Transfers',
    description: 'Punctual and seamless transportation to and from airports with flight tracking and meet-and-greet service.',
    image: '/images/services/airport-transfer.jpg',
    icon: <AirplanemodeActiveIcon fontSize="large" />,
    features: ['Multiple Vehicle Classes', '24/7 Availability', 'Meet & Greet']
  },
  {
    id: 2,
    title: 'Corporate Travel',
    description: 'Reliable transportation for business meetings, client visits, and corporate retreats with options for every budget.',
    image: '/images/services/corporate-travel.jpg',
    icon: <BusinessIcon fontSize="large" />,
    features: ['Multiple Vehicle Classes', 'Professional Drivers', 'Corporate Accounts']
  },
  {
    id: 3,
    title: 'Event Transportation',
    description: 'Coordinated group transportation for conferences, galas, and social events with custom logistics planning.',
    image: '/images/services/event-transportation.jpg',
    icon: <EventIcon fontSize="large" />,
    features: ['Group Coordination', 'Multiple Pickup Points', 'Dedicated Event Manager', 'Fleet Variety']
  },
  {
    id: 4,
    title: 'Hourly Services',
    description: 'Flexible transportation solutions with dedicated vehicles available by the hour, from standard to luxury options.',
    image: '/images/services/hourly-service.jpg',
    icon: <AccessTimeIcon fontSize="large" />,
    features: ['Minimum 2-Hour Booking', 'Multiple Stops', 'Customizable Itinerary', 'No Hidden Fees']
  }
];

const ServiceHighlights: React.FC = () => {
  const theme = useTheme();

  // Section title styling consistent with HomePage
  const sectionTitleStyles = {
    textAlign: 'center',
    mb: { xs: 6, md: 10 },
    mx: 'auto',
    maxWidth: 900
  };

  // Section subtitle styling
  const sectionSubtitleStyles = {
    color: 'secondary.main',
    fontWeight: 600,
    mb: 1,
    letterSpacing: 1,
    textTransform: 'uppercase',
    textAlign: 'center'
  };

  // Section heading styling
  const sectionHeadingStyles = {
    fontFamily: 'Playfair Display, serif',
    fontWeight: 600,
    mb: 3,
    fontSize: { xs: '2.25rem', md: '3rem' },
    textAlign: 'center'
  };

  // Section description styling
  const sectionDescriptionStyles = {
    color: 'text.secondary',
    maxWidth: 800,
    mx: 'auto',
    fontSize: { xs: '1rem', md: '1.1rem' },
    lineHeight: 1.7,
    textAlign: 'center'
  };

  return (
    <Box
      sx={{
        py: { xs: 8, md: 12 },
        background: `linear-gradient(180deg, #ffffff 0%, ${alpha(theme.palette.primary.light, 0.05)} 100%)`,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background decorative elements */}
      <Box
        sx={{
          position: 'absolute',
          top: -100,
          right: -100,
          width: 300,
          height: 300,
          borderRadius: '50%',
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.15)} 0%, ${alpha(theme.palette.primary.main, 0.15)} 100%)`,
          zIndex: 0,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: -100,
          left: -100,
          width: 250,
          height: 250,
          borderRadius: '50%',
          background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.light, 0.15)} 0%, ${alpha(theme.palette.secondary.main, 0.15)} 100%)`,
          zIndex: 0,
        }}
      />

      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
        <Box sx={sectionTitleStyles}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <Typography
              variant="h6"
              component="p"
              sx={sectionSubtitleStyles}
            >
              OUR SERVICES
            </Typography>
            <Typography
              variant="h3"
              component="h2"
              sx={sectionHeadingStyles}
            >
              Complete Transportation Solutions
            </Typography>
            <Typography
              variant="body1"
              sx={sectionDescriptionStyles}
            >
              We offer a comprehensive range of transportation services designed to meet the 
              diverse needs of all clients. From standard vehicles to luxury options, experience 
              reliability, comfort, and professionalism with every journey.
            </Typography>
          </motion.div>
        </Box>

        <GridContainer spacing={{ xs: 4, md: 5 }} justifyContent="center">
          {services.map((service, index) => (
            <GridItem key={service.id} xs={12} sm={6} md={6} lg={3}>
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ 
                  duration: 0.7, 
                  delay: index * 0.15,
                  ease: "easeOut"
                }}
                viewport={{ once: true, margin: "-50px" }}
                style={{ height: '100%' }}
              >
                <Card
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    width: '100%',
                    borderRadius: 4,
                    overflow: 'hidden',
                    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.08)',
                    transition: 'all 0.4s ease',
                    position: 'relative',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      width: '100%',
                      height: '5px',
                      background: `linear-gradient(90deg, ${theme.palette.secondary.main}, ${theme.palette.primary.main})`,
                      transform: 'scaleX(0)',
                      transformOrigin: 'right',
                      transition: 'transform 0.4s ease',
                    },
                    '&:hover': {
                      transform: 'translateY(-16px)',
                      boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
                      '&::before': {
                        transform: 'scaleX(1)',
                        transformOrigin: 'left',
                      }
                    },
                  }}
                >
                  <Box sx={{ position: 'relative', height: '220px', overflow: 'hidden' }}>
                    <CardMedia
                      component="img"
                      height="220"
                      image={service.image}
                      alt={service.title}
                      sx={{
                        transition: 'transform 0.7s ease',
                        objectFit: 'cover',
                        objectPosition: 'center',
                        '&:hover': {
                          transform: 'scale(1.05)',
                        }
                      }}
                    />
                    <Box 
                      sx={{ 
                        position: 'absolute',
                        bottom: -30,
                        right: 30,
                        zIndex: 2,
                      }}
                    >
                      <FloatingAnimation distance={8} duration={4} delay={index * 0.2}>
                        <Box
                          sx={{
                            bgcolor: theme.palette.secondary.main,
                            color: 'white',
                            width: 70,
                            height: 70,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 10px 20px rgba(0, 0, 0, 0.15)',
                            border: '4px solid white',
                          }}
                        >
                          {service.icon}
                        </Box>
                      </FloatingAnimation>
                    </Box>
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        background: 'linear-gradient(0deg, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0) 40%)',
                      }}
                    />
                  </Box>
                  <CardContent sx={{ pt: 4, flexGrow: 1, px: { xs: 3, md: 4 }, display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="h5" component="h3" sx={{ 
                      fontWeight: 700, 
                      mb: 2,
                      color: theme.palette.text.primary,
                      height: '60px',
                      display: 'flex',
                      alignItems: 'center',
                    }}>
                      {service.title}
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      color: theme.palette.text.secondary, 
                      mb: 3,
                      lineHeight: 1.6,
                      minHeight: '80px',
                    }}>
                      {service.description}
                    </Typography>
                    <Box sx={{ mb: 3, minHeight: '160px' }}>
                      {service.features.map((feature, idx) => (
                        <Typography 
                          key={idx} 
                          variant="body2" 
                          component="div" 
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            mb: 1.5,
                          }}
                        >
                          <Box 
                            component="span" 
                            sx={{ 
                              minWidth: 26,
                              height: 26, 
                              backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                              borderRadius: '50%', 
                              mr: 1.5,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <CheckIcon
                              fontSize="small"
                              sx={{ 
                                color: theme.palette.secondary.main,
                                fontSize: '0.9rem',
                              }}
                            />
                          </Box>
                          <span>{feature}</span>
                        </Typography>
                      ))}
                    </Box>
                  </CardContent>
                  <CardActions sx={{ p: 0, pb: 2, px: { xs: 3, md: 4 }, height: '60px' }}>
                    <motion.div
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.98 }}
                      style={{ width: '100%' }}
                    >
                      <Button 
                        component={RouterLink}
                        to={`/services#${service.id}`}
                        variant="contained" 
                        color="primary"
                        fullWidth
                        endIcon={<ArrowForwardIcon />}
                        sx={{
                          py: 1.2,
                          fontWeight: 600,
                          borderRadius: 10,
                          textTransform: 'none',
                          fontSize: '0.95rem',
                          boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
                        }}
                      >
                        Learn More
                      </Button>
                    </motion.div>
                  </CardActions>
                </Card>
              </motion.div>
            </GridItem>
          ))}
        </GridContainer>

        <Box
          sx={{
            mt: { xs: 6, md: 8 },
            textAlign: 'center',
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            viewport={{ once: true }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Button
              component={RouterLink}
              to="/services"
              variant="contained"
              color="secondary"
              size="large"
              endIcon={<ArrowForwardIcon />}
              sx={{
                px: 5,
                py: 1.5,
                fontWeight: 600,
                borderRadius: 30,
                textTransform: 'none',
                fontSize: '1.1rem',
                boxShadow: '0 8px 20px rgba(0, 0, 0, 0.15)',
              }}
            >
              Explore All Services
            </Button>
          </motion.div>
        </Box>
      </Container>
    </Box>
  );
};

export default ServiceHighlights; 